/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Submit, capacity check, and confirmation flow for registration.
 * @module hooks/useAppointmentRegistrationSubmit
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { logger, sanitizeError } from "../utils/logger.js";
import { hasMissingRequiredFields } from "../utils/appointmentRegistrationUtils.js";
import { isRegistrationPastDue } from "../logic/appointments/appointmentRegistrationDate.js";
import {
  buildAppointmentRegistrationPayload,
  checkRegistrationCapacityAndProceed,
  createRegistrationAppointment,
  persistRegistrationCompanyChanges,
} from "../logic/appointments/appointmentRegistrationSave.js";

/**
 * Manages save/submit state and handlers for appointment registration.
 * @param {Object} options
 * @returns {Object}
 */
const useAppointmentRegistrationSubmit = ({
  currentUser,
  participant,
  selectedRequirement,
  displayCompany,
  company,
  formData,
  missingRequiredFields,
  eventData,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isCheckingAppointments, setIsCheckingAppointments] = useState(false);
  const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    setShowRequiredFieldErrors(false);
    setShowConfirmationModal(false);
  }, [selectedRequirement?._id]);

  const handleSave = useCallback(
    async (setCompany) => {
      if (loading) {
        return;
      }
      setLoading(true);
      try {
        const isNoSocio = String(currentUser?.tipoDeUsuario) === "4";
        const { appointmentData, newFileFields, regularFields, fileFields } =
          buildAppointmentRegistrationPayload({
            participant,
            selectedRequirement,
            displayCompany,
            company,
            formData,
          });

        const { updatedCompany } = await persistRegistrationCompanyChanges({
          company,
          regularFields,
          fileFields,
          isNoSocio,
          navigate,
        });

        if (updatedCompany) {
          setCompany(updatedCompany);
        }

        await createRegistrationAppointment({
          navigate,
          appointmentData,
          newFileFields,
        });

        onSubmit();
      } catch (error) {
        logger.error("Error saving changes:", { err: sanitizeError(error) });
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      currentUser,
      participant,
      selectedRequirement,
      displayCompany,
      company,
      formData,
      navigate,
      onSubmit,
    ],
  );

  const checkAppointmentsAndProceed = useCallback(
    async (setCompany) => {
      if (submitInFlightRef.current || loading || isCheckingAppointments) {
        return;
      }

      if (hasMissingRequiredFields(missingRequiredFields)) {
        setShowRequiredFieldErrors(true);
        return;
      }

      setShowRequiredFieldErrors(false);
      submitInFlightRef.current = true;
      setIsCheckingAppointments(true);

      if (isRegistrationPastDue(eventData?.fechaLimiteRegistro)) {
        setIsCheckingAppointments(false);
        submitInFlightRef.current = false;
        return;
      }

      try {
        const result = await checkRegistrationCapacityAndProceed({
          navigate,
          eventData,
          onSave: () => handleSave(setCompany),
        });
        if (result === "confirm") {
          setShowConfirmationModal(true);
        }
      } finally {
        setIsCheckingAppointments(false);
        submitInFlightRef.current = false;
      }
    },
    [loading, isCheckingAppointments, missingRequiredFields, eventData, navigate, handleSave],
  );

  const handleConfirmationModalConfirm = useCallback(
    async (setCompany) => {
      await handleSave(setCompany);
    },
    [handleSave],
  );

  const handleConfirmationModalCancel = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  return {
    loading,
    showConfirmationModal,
    isCheckingAppointments,
    showRequiredFieldErrors,
    handleSave,
    checkAppointmentsAndProceed,
    handleConfirmationModalConfirm,
    handleConfirmationModalCancel,
  };
};

export default useAppointmentRegistrationSubmit;
